import { Component, OnInit, ViewChild } from '@angular/core';
import { GeneralExerciseComponent } from '../../General/g-exercise.component';
import { ActivatedRoute } from '@angular/router';
import { TopicService } from '../topic.service';
import { MessageService } from '../../shared/message.service';
import { CryptoService } from '../crypto.service';
import { CaesarComponent } from '../caesar/caesar.component';
import { InputFieldComponent } from '../input-field/input-field.component';
import { PolybiusComponent } from '../polybius/polybius.component';
import { CryptoView } from '../crypto-view';
import { SkytaleComponent } from '../skytale/skytale.component';
import { MonoalphabeticComponent } from '../monoalphabetic/monoalphabetic.component';
import { VignereComponent } from '../vignere/vignere.component';
import { PigpenComponent } from '../pigpen/pigpen.component';
import { AtbashComponent } from '../atbash/atbash.component';
import { MorseComponent } from '../morse/morse.component';
import { morseCode } from '../../../assets/morse';

@Component({
  selector: 'app-decode-text',
  templateUrl: './decode-text.component.html',
  styleUrls: ['../topic-style.css', './decode-text.component.css']
})
export class DecodeTextComponent extends GeneralExerciseComponent {

  @ViewChild('caesar', {static: true})
  private caesar: CaesarComponent;

  @ViewChild('polybius', {static: true})
  private polybius: PolybiusComponent;

  @ViewChild('skytale', {static: true})
  private skytale: SkytaleComponent;

  @ViewChild('monoalphabetic', {static: true})
  private monoalphabetic: MonoalphabeticComponent;

  @ViewChild('vignere', {static: true})
  private vignere: VignereComponent;

  @ViewChild('pigpen', {static: true})
  private pigpen: PigpenComponent;

  @ViewChild('atbash', {static: true})
  private atbash: AtbashComponent;

  @ViewChild('morse', {static: true})
  private morse: MorseComponent;

  private view: CryptoView;

  @ViewChild('inputField', {static: true})
  private inputField: InputFieldComponent;

  plainText: string;
  cipherText: string;
  method: string;
  keyP: any;
  modus: string;

  dCipherText: string[];

  caretPos: number = 0;
  positionLetter: string;

  errors: number[] = [];


  constructor(activatedRoute: ActivatedRoute, topicService: TopicService, messageService: MessageService, protected cryptoService: CryptoService) {
    super(activatedRoute, topicService, messageService);
    this.printable = true;
    this.type = "decode";
  }

  setTypeSpecificsFromConfiguration() {
    this.cipherText = this.configuration.typespecifics.text;
    this.dCipherText = this.cipherText.split("");
    this.method = this.configuration.typespecifics.method;
    this.keyP = this.configuration.typespecifics.key;
    this.plainText = this.cryptoService.decode(this.method, this.keyP, this.cipherText.replace(/#/gi, " ")).replace(/[A-z0-9]/gi, "#");
    this.modus = this.configuration.typespecifics.modus;
    switch (this.method) {
      case "caesar": this.view = this.caesar; break;
      case "polybius": this.view = this.polybius; break;
      case "skytale": this.view = this.skytale; break;
      case "monoalphabetic": this.view = this.monoalphabetic; break;
      case "vignere": this.view = this.vignere; break;
      case "pigpen": this.view = this.pigpen; break;
      case "atbash": this.view = this.atbash; break;
      case "morse": this.view = this.morse; break;
    }

    this.caretPos = 0;
    while (this.cipherText.charAt(this.caretPos) === " " && this.caretPos > 0) {
      this.caretPos++;
    }

    this.onEmit(this.caretPos);
    if (this.view) {
      this.view.resetmapping();
    }
  }

  setTypeSpecificsFromState() {
    if (this.state.typespecific) {
      if (this.state.typespecific.plainText) {
        this.plainText = this.state.typespecific.plainText;
      }
      if (this.state.typespecific.mapping) {
        this.view.setmapping(this.state.typespecific.mapping);
      }
    }
  }

  computeState() {
    this.plainText = this.inputField.getText();
    this.state.typespecific.plainText = this.plainText;
    if (this.modus == "noMapping") {
      this.state.typespecific.mapping = this.view.getMapping();
    }
  }

  /** when decoded text changes, recompute the position letter */
  onEmit(event) {
    this.caretPos = event;
    this.positionLetter = this.cipherText.charAt(this.caretPos);
    if (this.method == "polybius") {
      this.positionLetter = this.cipherText.substring(this.caretPos, this.caretPos + 2);
    }
    if (this.method == "skytale") {
      this.positionLetter = this.caretPos.toString();
      let currentColumn = Math.floor(+this.caretPos % Math.ceil(this.plainText.length / this.keyP))
      let currentRow = (Math.floor(+this.caretPos /  Math.ceil(this.plainText.length / this.keyP)));

      this.caretPos = currentColumn * this.keyP + currentRow; 
      if(this.modus != "highlight")
      this.caretPos = -1;
    }
    if (this.method == "vignere") {
      this.positionLetter = this.caretPos.toString();
    }
    if (this.method == "morse") {
      this.positionLetter = this.cryptoService.decode(this.method, null, this.cipherText.split("|")[this.caretPos]);
    }
  }

  evaluate(): number[] {
    let solution = this.cryptoService.decode(this.method, this.keyP, this.cipherText);
    this.plainText = this.inputField.getText();
    let errors = solution.split("").map((v, i) => [v, this.plainText.charAt(i), i]).filter(v => v[0] != v[1]).map(v => +v[2]);
    let result = [(this.plainText.length - errors.length) / 20, this.plainText.length / 20];
    if (this.modus == 'highlight') {
      result = result.map(v => v / 2);
    }
    if (this.method == "vignere") {
      result = result.map(v => v * 1.5);
    }
    return result;
  }

  checkSolution() {
    let solution = this.cryptoService.decode(this.method, this.keyP, this.cipherText);
    this.plainText = this.inputField.getText();
    this.errors = solution.split("").map((v, i) => [v, this.plainText.charAt(i), i]).filter(v => v[0] != v[1]).map(v => +v[2]);
    if (this.method == "polybius") {
      this.errors = this.errors.map((v, i) => (v - this.plainText.substring(0, v).replace(/[^ ]/gi, "").length) * 2 + this.plainText.substring(0, v).replace(/[^ ]/gi, "").length);
    }
    if (this.method == "skytale") {
      this.errors = this.errors.map((v, i) => Math.floor(v % (Math.floor(this.plainText.length / this.keyP))) * (this.keyP) + Math.floor(v / ((Math.floor(this.plainText.length / this.keyP)))));
    }
    if (this.method == 'morse') {
      let temp = this.errors.map(v => this.createArray(this.cipherText.split("|")[v].length).map((u) => this.cipherText.split("|").slice(0, v).join("|").length + 1 + u));
      this.errors = [].concat(...temp);
      console.log(this.errors);
    }
    if (this.errors.length > 0) {
      this.messageService.addAlertWithTimeout(this.get("notCorrectlyDecoded"), "danger", 10000);
    } else {
      this.messageService.addAlertWithTimeout(this.get("correctlyDecoded"), "success", 10000);
      this.solved = true;
      this.state.solved = true;
      this.saveState();
    }
  }

  /** returns the correct method description */
  getMethodDescription() {
    switch (this.method) {
      case "caesar": return this.get("useCaesar");
      case "polybius": return this.get("usePolybius");
      case "skytale": return this.get("useSkytale");
      case "monoalphabetic": return this.get("useMonoalphabetic");
      case "vignere": return this.get("useMask");
      case "pigpen": return this.get("usePigpen");
      case "atbash": return this.get("useAtbash");
      case "morse": return this.get("useMorse");
    }

  }


}
